import { useState, useEffect, useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Send, Zap, Trash2, Loader2, Save, CheckCircle2, Copy, Inbox as InboxIcon, Mail } from "lucide-react";
import { api, streamChat } from "@/lib/api";
import { initials, PERSONALITIES, AUTONOMY_LEVELS, TOOLS } from "@/lib/lifeos";
import { avatarFor } from "@/lib/avatars";

const selectCls = "w-full bg-[#0b0e14] border border-white/10 rounded-md px-3 h-10 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40";

export default function AgentDrawer({ agent, dept, departments, live, onClose, onRunTask, onUpdate, onDelete, googleConnected, onDesign }) {
  const [tab, setTab] = useState("overview");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [taskInput, setTaskInput] = useState("");
  const [running, setRunning] = useState(false);
  const [lastTask, setLastTask] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [draft, setDraft] = useState(null); // {email, to, subject, body, thread_id, in_reply_to, drafting, sending}
  const [calInput, setCalInput] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [work, setWork] = useState(null);
  const [openWork, setOpenWork] = useState(null);
  const chatEndRef = useRef(null);

  const scheduleEvent = async () => {
    if (!calInput.trim() || scheduling) return;
    setScheduling(true);
    try {
      const ev = await api.agentCalendar(agent.id, calInput.trim());
      toast.success(`Scheduled: ${ev.summary}`, { description: new Date(ev.start).toLocaleString() });
      setCalInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule");
    } finally {
      setScheduling(false);
    }
  };

  const loadInbox = () => {
    setInboxLoading(true);
    api.googleInbox().then((d) => setInbox(d.messages || [])).catch(() => setInbox([])).finally(() => setInboxLoading(false));
  };

  const startDraft = async (email) => {
    setDraft({ email, drafting: true });
    try {
      const d = await api.draftReply(agent.id, email.id, "");
      setDraft({ email, ...d, drafting: false });
    } catch {
      toast.error("Could not draft reply");
      setDraft(null);
    }
  };

  const sendReply = async () => {
    setDraft((d) => ({ ...d, sending: true }));
    try {
      await api.googleSend({ to: draft.to, subject: draft.subject, body: draft.body, thread_id: draft.thread_id, in_reply_to: draft.in_reply_to });
      toast.success("Reply sent");
      setDraft(null);
    } catch {
      toast.error("Send failed");
      setDraft((d) => ({ ...d, sending: false }));
    }
  };

  useEffect(() => {
    if (!agent) return;
    setTab("overview");
    setLastTask(null);
    setInbox(null);
    setDraft(null);
    setWork(null);
    setOpenWork(null);
    api.getDeliverables().then((d) => setWork((d || []).filter((t) => t.agent_id === agent.id).slice(0, 10))).catch(() => setWork([]));
    setCfg({
      role: agent.role, personality: agent.personality, autonomy: agent.autonomy,
      goals: agent.goals || "", department_id: agent.department_id, connected_tools: agent.connected_tools || [],
      autopilot: agent.autopilot || false, autopilot_interval: agent.autopilot_interval || 15, autopilot_task: agent.autopilot_task || "", autopilot_mode: agent.autopilot_mode || "task",
      auto_fire_automations: agent.auto_fire_automations || false,
    });
    api.getMessages(agent.id).then(setMessages).catch(() => setMessages([]));
  }, [agent]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => {
    if (tab === "inbox" && googleConnected && inbox === null && !inboxLoading) loadInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, googleConnected]);

  if (!agent) {
    return <div className="fixed right-0 top-16 bottom-0 w-full sm:w-[440px] translate-x-full pointer-events-none z-50" />;
  }

  const accent = agent.accent || "#00f0ff";
  const phase = live?.phase || "idle";
  const status = live?.status || "Standing by";
  const progress = live?.progress || 0;

  const sendChat = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamChat(
        agent.id,
        msg,
        (delta) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        }),
        () => setStreaming(false)
      );
    } catch {
      setStreaming(false);
      toast.error("Agent uplink failed");
    }
  };

  const assignTask = async () => {
    const t = taskInput.trim();
    if (!t || running) return;
    setRunning(true);
    setLastTask(null);
    try {
      const task = await onRunTask(agent.id, t);
      setLastTask(task);
      setTaskInput("");
      toast.success(`${agent.name} is executing the task`);
    } catch {
      toast.error("Task dispatch failed");
    } finally {
      setRunning(false);
    }
  };

  const saveCfg = async () => {
    try {
      await onUpdate(agent.id, cfg);
      toast.success("Agent reconfigured");
    } catch {
      toast.error("Update failed");
    }
  };

  const setC = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleTool = (t) =>
    setCfg((c) => ({ ...c, connected_tools: c.connected_tools.includes(t) ? c.connected_tools.filter((x) => x !== t) : [...c.connected_tools, t] }));

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 sm:hidden" onClick={onClose} />
      <aside
        data-testid="agent-drawer"
        className="fixed right-0 top-16 bottom-0 w-full sm:w-[440px] z-50 backdrop-blur-2xl bg-[#080b11]/97 border-l border-white/10 shadow-2xl flex flex-col anim-slidein"
      >
        {/* header */}
        <div className="p-5 border-b border-white/10 relative" style={{ background: `linear-gradient(180deg, ${accent}12, transparent)` }}>
          <button data-testid="agent-drawer-close-button" onClick={onClose} className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400">
            <X size={16} />
          </button>
          <div className="flex items-center gap-3">
            <div className="rounded-full overflow-hidden shrink-0" style={{ width: 56, height: 56, border: `2px solid ${accent}`, boxShadow: `0 0 20px ${accent}55`, background: "#0b0e14" }}>
              <img src={avatarFor(agent)} alt={agent.name} className="w-full h-full object-cover object-top" draggable="false" />
            </div>
            <div>
              <h2 className="font-tactical font-bold text-xl text-zinc-100 leading-tight">{agent.name}</h2>
              <div className="font-mono text-[11px] text-zinc-500">{agent.codename} · {agent.role}</div>
              <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded" style={{ background: `${dept?.color || accent}1f`, color: dept?.color || accent }}>
                {dept?.name}
              </div>
            </div>
          </div>

          {/* live status */}
          <div className="mt-4 rounded-xl bg-black/30 border border-white/10 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Live Status</span>
              <span className="text-[10px] font-mono px-1.5 rounded" style={{ color: phase === "complete" ? "#36f08a" : accent, background: phase === "idle" ? "rgba(255,255,255,0.04)" : `${accent}18` }}>
                {phase.toUpperCase()}
              </span>
            </div>
            <div className="mt-1.5 font-mono text-sm" style={{ color: phase === "complete" ? "#36f08a" : "#dbeafe" }}>{status}</div>
            {(phase === "working") && (
              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${progress}%`, background: accent, transition: "width .6s linear" }} />
              </div>
            )}
          </div>
        </div>

        {/* tabs */}
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-4 mt-3 bg-white/[0.04] border border-white/5 grid grid-cols-4">
            <TabsTrigger data-testid="tab-overview" value="overview" className="text-xs data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300">Overview</TabsTrigger>
            <TabsTrigger data-testid="tab-terminal" value="terminal" className="text-xs data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300">Terminal</TabsTrigger>
            <TabsTrigger data-testid="tab-inbox" value="inbox" className="text-xs data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300">Inbox</TabsTrigger>
            <TabsTrigger data-testid="tab-config" value="config" className="text-xs data-[state=active]:bg-cyan-500/15 data-[state=active]:text-cyan-300">Config</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-3 space-y-4 m-0">
            <div data-testid="agent-real-work">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-mono mb-2"><InboxIcon size={12} /> Real work {work ? `(${work.length})` : ""}</div>
              {work === null ? (
                <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono py-3"><Loader2 size={13} className="animate-spin" /> Loading {agent.name}'s output…</div>
              ) : work.length === 0 ? (
                <p className="text-xs text-zinc-600 leading-relaxed">No completed work yet. Assign a live task below and {agent.name} will produce a real deliverable you can open here.</p>
              ) : (
                <div className="space-y-1.5">
                  {work.map((t) => (
                    <div key={t.id} data-testid={`agent-work-${t.id}`} className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                      <button onClick={() => setOpenWork(openWork === t.id ? null : t.id)} className="w-full text-left px-3 py-2 hover:bg-white/5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-100 flex-1 truncate">{t.instruction || t.result || "Deliverable"}</span>
                          {t.source === "boss" && <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 shrink-0">ATLAS</span>}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                      </button>
                      {openWork === t.id && (
                        <div className="px-3 pb-3 anim-slidein">
                          {t.result && <p className="text-xs text-zinc-400 mb-2 leading-relaxed">{t.result}</p>}
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[9px] uppercase tracking-[0.2em] text-cyan-300 font-mono">Deliverable</span>
                            <button data-testid={`agent-work-copy-${t.id}`} onClick={() => { navigator.clipboard.writeText(t.deliverable || ""); toast.success("Copied"); }} className="text-[10px] font-mono text-zinc-400 hover:text-cyan-300 flex items-center gap-1"><Copy size={11} /> Copy</button>
                          </div>
                          <pre className="text-[12px] leading-relaxed text-zinc-200 whitespace-pre-wrap font-sans bg-[#0b0e14] border border-white/10 rounded-lg p-3 max-h-64 overflow-y-auto thin-scroll">{t.deliverable}</pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="h-px bg-white/5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">Goals</div>
              <p className="text-sm text-zinc-300 leading-relaxed">{agent.goals || "No goals defined yet."}</p>
            </div>
            {agent.schedule && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">Schedule</div>
                <p className="text-sm text-zinc-300 font-mono">{agent.schedule}</p>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">Connected Apps</div>
              <div className="flex flex-wrap gap-1.5">
                {(agent.connected_tools || []).length ? agent.connected_tools.map((t) => (
                  <span key={t} data-testid="app-connection-badge" className="text-[11px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">{t}</span>
                )) : <span className="text-xs text-zinc-600">None connected</span>}
              </div>
            </div>

            <Button data-testid="agent-create-design-button" onClick={() => onDesign(agent.id)} variant="outline" className="w-full border-cyan-500/25 bg-cyan-500/5 text-cyan-300 hover:bg-cyan-500/15 font-tactical uppercase text-xs">Create a flyer or poster in Studio</Button>

            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-3">
              <div className="flex items-center gap-1.5 text-cyan-300 text-[11px] font-tactical font-bold uppercase tracking-wider mb-2">
                <Zap size={13} /> Assign Live Task
              </div>
              <Textarea data-testid="agent-task-assign-input" rows={2} value={taskInput} onChange={(e) => setTaskInput(e.target.value)} placeholder="e.g. Draft a Q3 marketing plan and summarize it" className="bg-[#0b0e14] border-white/10 text-sm text-zinc-100 focus-visible:ring-cyan-400/40" />
              <Button data-testid="agent-task-assign-button" onClick={assignTask} disabled={running} className="mt-2 w-full bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase tracking-wider text-xs">
                {running ? <><Loader2 size={14} className="mr-1 animate-spin" /> Dispatching...</> : "Execute Task"}
              </Button>
              {lastTask && (
                <div className="mt-3 rounded-lg bg-black/30 border border-emerald-500/20 p-3 anim-slidein">
                  <div className="flex items-center gap-1.5 text-emerald-400 text-[11px] font-mono mb-1"><CheckCircle2 size={13} /> Task Complete</div>
                  <div className="space-y-1 mb-2">
                    {lastTask.steps.map((s, i) => (
                      <div key={i} className="text-[11px] font-mono text-zinc-400">→ {s}</div>
                    ))}
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed">{lastTask.result}</p>
                  {lastTask.deliverable && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-mono">Deliverable</span>
                        <button
                          data-testid="copy-deliverable-button"
                          onClick={() => { navigator.clipboard.writeText(lastTask.deliverable); toast.success("Copied to clipboard"); }}
                          className="text-[10px] font-mono text-zinc-400 hover:text-cyan-300 flex items-center gap-1"
                        >
                          <Copy size={11} /> Copy
                        </button>
                      </div>
                      <pre data-testid="deliverable-content" className="text-[12px] leading-relaxed text-zinc-200 whitespace-pre-wrap font-sans bg-[#0b0e14] border border-white/10 rounded-lg p-3 max-h-72 overflow-y-auto thin-scroll">{lastTask.deliverable}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TERMINAL */}
          <TabsContent value="terminal" className="flex-1 min-h-0 flex flex-col m-0">
            <div className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-3 space-y-3" data-testid="agent-terminal">
              {messages.length === 0 && (
                <div className="text-center text-zinc-600 text-xs font-mono mt-8">Open a channel with {agent.name}. Ask a question or give direction.</div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-cyan-500/15 text-cyan-50 border border-cyan-400/20" : "bg-white/[0.04] text-zinc-200 border border-white/10"}`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <span className="text-zinc-500">▍</span> : "")}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <Input
                data-testid="agent-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder={`Message ${agent.name}...`}
                className="bg-[#0b0e14] border-white/10 text-sm text-zinc-100 focus-visible:ring-cyan-400/40"
              />
              <Button data-testid="agent-chat-send-button" onClick={sendChat} disabled={streaming} className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] px-3">
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </TabsContent>

          {/* INBOX */}
          <TabsContent value="inbox" className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-3 m-0" data-testid="agent-inbox">
            {!googleConnected ? (
              <div className="text-center text-zinc-500 text-xs font-mono mt-8 px-4">
                <Mail size={26} className="mx-auto mb-3 text-zinc-600" />
                Connect Google in <span className="text-cyan-300">App Connections</span> to let {agent.name} read your inbox and draft replies.
              </div>
            ) : draft ? (
              <div className="space-y-2 anim-slidein">
                <button onClick={() => setDraft(null)} className="text-[11px] font-mono text-zinc-400 hover:text-cyan-300">← Back to inbox</button>
                <div className="rounded-lg bg-black/30 border border-white/10 p-2 text-[11px] font-mono text-zinc-500">Re: {draft.email.subject}</div>
                {draft.drafting ? (
                  <div className="flex items-center gap-2 text-zinc-400 text-xs font-mono py-6 justify-center"><Loader2 size={14} className="animate-spin" /> {agent.name} is drafting a reply...</div>
                ) : (
                  <>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">To</label>
                      <Input className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Subject</label>
                      <Input className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Reply (review before sending)</label>
                      <Textarea data-testid="reply-body" rows={9} className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
                    </div>
                    <Button data-testid="approve-send-button" onClick={sendReply} disabled={draft.sending} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04070c] font-tactical font-bold uppercase text-xs">
                      {draft.sending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Sending...</> : <><Send size={14} className="mr-1" /> Approve & Send</>}
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="rounded-lg border border-violet-500/25 bg-violet-500/[0.05] p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-violet-300 font-tactical font-bold mb-1.5">Quick schedule (Calendar)</div>
                  <div className="flex gap-2">
                    <Input data-testid="calendar-input" value={calInput} onChange={(e) => setCalInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scheduleEvent()} placeholder="e.g. Team sync tomorrow 3pm for 30 min" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm h-9" />
                    <Button data-testid="calendar-create" onClick={scheduleEvent} disabled={scheduling} className="h-9 bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase text-xs px-3">{scheduling ? <Loader2 size={14} className="animate-spin" /> : "Add"}</Button>
                  </div>
                </div>
                {inboxLoading && <div className="flex items-center gap-2 text-zinc-400 text-xs font-mono py-6 justify-center"><Loader2 size={14} className="animate-spin" /> Loading inbox...</div>}
                {inbox && inbox.length === 0 && !inboxLoading && <div className="text-center text-zinc-600 text-xs font-mono py-8">Inbox is empty.</div>}
                {(inbox || []).map((m) => (
                  <div key={m.id} className="rounded-lg bg-white/[0.02] border border-white/10 p-3">
                    <div className="text-sm text-zinc-100 truncate font-medium">{m.subject}</div>
                    <div className="text-[11px] font-mono text-zinc-500 truncate">{m.from}</div>
                    <div className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{m.snippet}</div>
                    <Button data-testid="draft-reply-button" onClick={() => startDraft(m)} size="sm" className="mt-2 h-7 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-400/30 text-[11px] font-tactical font-bold uppercase"><Mail size={12} className="mr-1" /> Draft reply</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CONFIG */}
          <TabsContent value="config" className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-3 space-y-3 m-0">
            {cfg && (
              <>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Role</Label>
                  <Input className="bg-[#0b0e14] border-white/10 text-zinc-100" value={cfg.role} onChange={(e) => setC("role", e.target.value)} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Department</Label>
                  <select className={selectCls} value={cfg.department_id} onChange={(e) => setC("department_id", e.target.value)}>
                    {departments.map((d) => <option key={d.id} value={d.id} className="bg-[#0b0e14]">{d.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Personality</Label>
                    <select className={selectCls} value={cfg.personality} onChange={(e) => setC("personality", e.target.value)}>
                      {PERSONALITIES.map((p) => <option key={p} value={p} className="bg-[#0b0e14]">{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Autonomy</Label>
                    <select className={selectCls} value={cfg.autonomy} onChange={(e) => setC("autonomy", e.target.value)}>
                      {AUTONOMY_LEVELS.map((a) => <option key={a} value={a} className="bg-[#0b0e14]">{a}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Goals</Label>
                  <Textarea rows={3} className="bg-[#0b0e14] border-white/10 text-zinc-100" value={cfg.goals} onChange={(e) => setC("goals", e.target.value)} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-zinc-400 font-tactical">Connected Apps</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {TOOLS.map((t) => {
                      const on = cfg.connected_tools.includes(t);
                      return (
                        <button type="button" key={t} onClick={() => toggleTool(t)} className={`px-2 py-1 rounded-lg text-[11px] font-mono border transition-all ${on ? "bg-cyan-500/15 text-cyan-300 border-cyan-400/40" : "bg-white/[0.03] text-zinc-500 border-white/10"}`}>{t}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button data-testid="agent-save-button" onClick={saveCfg} className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs"><Save size={14} className="mr-1" /> Save</Button>
                  <Button data-testid="agent-delete-button" onClick={() => { if (window.confirm(`Decommission ${agent.name}?`)) onDelete(agent.id); }} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={15} /></Button>
                </div>

                <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-3 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-amber-300 text-[11px] font-tactical font-bold uppercase tracking-wider"><Zap size={13} /> Always-On Autopilot</div>
                    <Switch data-testid="autopilot-switch" checked={cfg.autopilot} onCheckedChange={(v) => setC("autopilot", v)} className="data-[state=checked]:bg-amber-500" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mb-2 leading-snug">When on, this agent autonomously runs its task on a recurring schedule — even while you're away.</p>
                  {cfg.autopilot && (
                    <div className="space-y-2 anim-slidein">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Autopilot mode</Label>
                        <select data-testid="autopilot-mode-select" className={selectCls} value={cfg.autopilot_mode} onChange={(e) => setC("autopilot_mode", e.target.value)}>
                          <option value="task" className="bg-[#0b0e14]">Run a recurring task</option>
                          <option value="email_drafts" className="bg-[#0b0e14]">Draft email replies (needs Google)</option>
                        </select>
                      </div>
                      {cfg.autopilot_mode === "task" && (
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Recurring Task</Label>
                          <Input data-testid="autopilot-task-input" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm" value={cfg.autopilot_task} onChange={(e) => setC("autopilot_task", e.target.value)} placeholder="e.g. Scan inbox and flag urgent emails" />
                        </div>
                      )}
                      {cfg.autopilot_mode === "email_drafts" && (
                        <p className="text-[10px] font-mono text-amber-400/70">This agent will pre-draft replies to new unread emails into your Approvals queue.</p>
                      )}
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Every (minutes)</Label>
                        <Input data-testid="autopilot-interval-input" type="number" min={3} className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm w-28" value={cfg.autopilot_interval} onChange={(e) => setC("autopilot_interval", parseInt(e.target.value || "15", 10))} />
                      </div>
                      <p className="text-[10px] font-mono text-amber-400/70">Save to apply. Minimum 3 minutes.</p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-3 mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-cyan-300 text-[11px] font-tactical font-bold uppercase tracking-wider"><Zap size={13} /> Auto-fire Automations</div>
                    <Switch data-testid="auto-fire-automations-switch" checked={cfg.auto_fire_automations} onCheckedChange={(v) => setC("auto_fire_automations", v)} className="data-[state=checked]:bg-cyan-500" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-2 leading-snug">When ON, this agent instantly fires matching connected automations after finishing a task. When OFF, matches wait in the Automations → Agent Queue for your approval. Money actions always require verification.</p>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </aside>
    </>
  );
}
